<script setup>
import RevealPhone from "@/Components/RevealPhone.vue";
import UslugiQuiz from "@/Components/UslugiQuiz.vue";
import MaxButton from "@/Components/MaxButton.vue";
import TelegramButton from "@/Components/TelegramButton.vue";
import { useModal, ModalsContainer } from "vue-final-modal";

// <ModalsContainer> в SSR не рендерит ничего, а на клиенте — фрагмент.
// Из-за этого число детей у корня страницы расходилось и Vue ругался
// «Hydration completed but contains mismatches» на КАЖДОЙ странице сайта.
// С v-if обе стороны отдают комментарий-заглушку, а контейнер появляется после монтирования.
const vfmReady = ref(false);
onMounted(() => { vfmReady.value = true; });

import { computed, ref, onMounted } from "vue";

let address = "Респ. Крым, г. Симферополь, ул. Долгоруковская, 5";
let phone = "+7 978 8838 978";

const props = defineProps({
  usl_name: String,
  usl_desc: String,
  address: String,
  dopadress: String,
  phone: String,
  maps: String,
  max: String,
  telegram: String,
  metaimage: String,
  company: String,
  usluga_id: Number,
  region: Object,
  backendurl: String,
  useTrackingPhone: Boolean,
  tracking: {
    type: Object,
    default: () => ({})
  },
  main_uslugi: { type: Array, default: () => [] },
  all_second_uslugi: { type: Array, default: () => [] },
  service_main_id: { type: Number, default: null },
  page_lawyer: { type: Object, default: null },
  quiz_lawyers: { type: Array, default: () => [] },
  quiz_service_questions: { type: Object, default: () => ({}) },
  // Страница услуги: на десктопе красный телефон уже есть в боковой колонке
  desktopPhoneSecondary: { type: Boolean, default: false },
  // Только телефон: без «Оставить заявку», MAX и Telegram. Страницы с онлайн-записью —
  // там заявку заменяет календарь (решение владельца 25.10.2026).
  onlyPhone: { type: Boolean, default: false },
});

const { open: openQuiz, close: closeQuiz } = useModal({
  component: UslugiQuiz,
  attrs: {
    get uslName() { return props.usl_name; },
    get lawyerName() { return props.company; },
    get uslugaId() { return props.usluga_id; },
    get cityUrl() { return props.region?.url ?? ''; },
    get allUslugi() { return props.main_uslugi; },
    get allSecondUslugi() { return props.all_second_uslugi; },
    get mainUslugaId() { return props.service_main_id; },
    get pageLawyer() { return props.page_lawyer; },
    get quizLawyers() { return props.quiz_lawyers; },
    get allServiceQuestions() { return props.quiz_service_questions; },
    onClose() { closeQuiz(); },
  },
});

function normalizePhone(raw) {
  if (!raw) return phone;
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('0')) return '+7' + digits.slice(1);
  if (digits.length === 11 && digits.startsWith('8')) return '+7' + digits.slice(1);
  if (digits.length === 11 && digits.startsWith('7')) return '+' + digits;
  if (digits.length === 10) return '+7' + digits;
  return raw;
}

if (props.address !== undefined) {
  address = props.address;
}
if (props.phone !== undefined) {
  phone = normalizePhone(props.phone);
}

// Показываем строку адреса только если это настоящий адрес (с улицей),
// а не просто повтор названия города (например "Краснодар") — иначе слово висит сиротой.
const hasRealAddress = computed(() => {
  const a = (address ?? "").trim();
  if (!a) return false;
  const city = (props.region?.title ?? "").trim();
  return a.toLowerCase() !== city.toLowerCase();
});

// Короткий адрес: без страны/региона, «город» убираем, «улица»→«ул.» —
// «Симферополь, ул. Долгоруковская, 5» вместо трёх строк с «Россия, Респ. Крым…».
const shortAddress = computed(() => {
  // ⚠ Без \b: в JS словарная граница работает только для ASCII, с кириллицей не ловит.
  let a = (address ?? "").trim();
  a = a.replace(/^\s*росси[яй]\s*,?\s*/i, "");                  // «Россия,»
  a = a.replace(/^\s*(респ(ублика|\.)?\s*)?крым\s*,?\s*/i, ""); // «Республика Крым,» / «Респ. Крым,»
  a = a.replace(/город\s+/gi, "");                             // «город Симферополь» → «Симферополь»
  a = a.replace(/(^|[,\s])г\.\s*/gi, "$1");                    // «г. Симферополь» → «Симферополь»
  a = a.replace(/улица\s+/gi, "ул. ");                        // «улица» → «ул.»
  return a.replace(/^[\s,]+/, "").replace(/\s{2,}/g, " ").trim();
});

// Ссылка на мессенджер MAX. Дописываем протокол, если юрист вставил без него.
const maxLink = computed(() => {
  const m = (props.max ?? "").trim();
  if (!m) return "";
  return /^https?:\/\//i.test(m) ? m : "https://" + m;
});

// Ссылка на Telegram (кнопка рядом с MAX). Пусто — кнопка не показывается.
const tgLink = computed(() => {
  const t = (props.telegram ?? "").trim();
  if (!t) return "";
  return /^https?:\/\//i.test(t) ? t : "https://" + t;
});

// Кнопки действий: Квиз и Телефон всегда, Я.Карты и MAX — по наличию.
// ⚠ РАСКЛАДКА ПО ВАРИАНТУ «А» ИЗ МАКЕТОВ (21.09.2026). Телефон — единственная
// красная кнопка, во всю ширину. Остальные второстепенные (белые с рамкой),
// на телефоне по две в ряд; если их нечётное число, последняя растягивается.
// Раньше заявка была зелёной: зелёный в системе — цвет статуса «получилось».
// Маршрут вынесен из сетки к строке адреса (см. шаблон). Квиз на телефоне скрыт,
// поэтому в сетке кнопок остаются: Телефон (главная), Квиз (только десктоп) и MAX.
const desktopBtns = computed(() => (props.onlyPhone ? 1 : 2 + (maxLink.value ? 1 : 0) + (tgLink.value ? 1 : 0))); // телефон + квиз + MAX + TG
const gridClass = computed(() => {
  const n = desktopBtns.value;
  if (n === 1) return "max-w-md lg:grid-cols-1";
  if (n >= 4) return "max-w-5xl lg:grid-cols-4";
  if (n === 3) return "max-w-3xl lg:grid-cols-3";
  return "max-w-2xl lg:grid-cols-2";
});
const secondaryBtn =
  "w-full h-full inline-flex items-center justify-center gap-2 py-2.5 px-3 text-sm font-semibold " +
  "text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors " +
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600";
</script>

<template>
  <!-- address -->
  <div class="md:flex justify-center px-2 pt-2 pb-0 md:p-2" id="contacts">
    <div class="w-full">
      <section>
        <div class="px-4 grid justify-items-center py-4 lg:py-6">
          <!-- На телефоне заголовок только для роботов и дикторов: кнопки должны
               встать сразу под фото, на первый экран -->
          <h2 class="sr-only lg:not-sr-only m-0 font-extrabold text-2xl text-gray-900 tracking-tight text-center lg:pb-6">
            Контакты
          </h2>

          <div id="main-cta" class="flex items-stretch gap-2 w-full lg:grid lg:gap-4" :class="gridClass">

            <!-- Квиз — только десктоп (на мобильном убран) -->
            <div v-if="!onlyPhone" class="hidden lg:block min-w-0 lg:order-1">
              <button type="button" @click="openQuiz" :class="secondaryBtn">
                <svg class="w-4 h-4 shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span class="whitespace-nowrap">Оставить заявку</span>
              </button>
            </div>

            <!-- Телефон — широкая красная кнопка (на мобильном растягивается) -->
            <div class="flex-1 min-w-0 lg:flex-none lg:order-2">
              <RevealPhone :key="backendurl" :tracking="$page.props.tracking" :backendurl="props.backendurl"
                :phone="phone" :phoneto="'tel:' + phone" :use-tracking-phone="props.useTrackingPhone"
                :lawyer="props.page_lawyer" :service-name="props.usl_name" :usl-desc="props.usl_desc"
                :desktop-secondary="props.desktopPhoneSecondary" />
            </div>

            <!-- Мессенджер MAX: квадратный значок на мобильном, кнопка на десктопе -->
            <template v-if="maxLink && !onlyPhone">
              <MaxButton :href="maxLink" icon-only class="lg:hidden w-14 shrink-0" />
              <div class="hidden lg:block min-w-0 lg:order-3">
                <MaxButton :href="maxLink" flat class="w-full h-full" />
              </div>
            </template>

            <!-- Telegram: квадратный значок на мобильном, кнопка на десктопе -->
            <template v-if="tgLink && !onlyPhone">
              <TelegramButton :href="tgLink" icon-only class="lg:hidden w-14 shrink-0" />
              <div class="hidden lg:block min-w-0 lg:order-4">
                <TelegramButton :href="tgLink" flat class="w-full h-full" />
              </div>
            </template>

          </div>

          <!-- Адрес сразу под кнопками (8px), затем описание юриста (16px) -->
          <div class="w-full max-w-3xl mt-2 space-y-4">
            <div v-if="hasRealAddress">
              <p v-if="dopadress" class="mb-1 text-left text-sm text-gray-500">Главный офис:</p>
              <!-- Адрес и маршрут — одним визуальным блоком -->
              <div class="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                <p class="m-0 flex-1 text-left text-sm text-gray-600">{{ shortAddress }}</p>
                <a v-if="props.maps" :href="props.maps" onclick="window.goal('YaMap_Click'); return true;" target="_blank"
                  class="h-11 w-11 shrink-0 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white shadow-sm transition-colors hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  aria-label="Проложить маршрут" title="Проложить маршрут">
                  <img class="h-6 w-6" src="https://yandex.ru/maps/favicon.svg" alt="" />
                </a>
              </div>
            </div>
            <p v-if="props.usl_desc" class="text-left text-sm leading-relaxed text-gray-600">
              {{ props.usl_desc }}
            </p>
          </div>

          <!-- Дополнительные офисы -->
          <div v-if="dopadress" class="mt-6 text-center">
            <p class="text-xs font-semibold text-gray-500 tracking-tight">Дополнительные офисы</p>
            <p v-html="dopadress" class="text-xs font-semibold text-gray-800 tracking-tight mt-1"></p>
          </div>

          <!-- Микроразметка schema.org -->
          <div itemscope itemtype="https://schema.org/Service" class="hidden">
            <div itemprop="provider" itemscope itemtype="https://schema.org/Person">
              <meta itemprop="name" :content="props.company">
              <meta itemprop="telephone" :content="phone">
              <div itemprop="workLocation" itemscope itemtype="https://schema.org/Place">
                <div itemprop="address" itemscope itemtype="https://schema.org/PostalAddress">
                  <meta itemprop="streetAddress" :content="address" />
                  <meta itemprop="addressCountry" content="RU" />
                  <meta itemprop="addressLocality" :content="region.region" />
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>
    </div>
  </div>
  <ModalsContainer v-if="vfmReady" />
</template>